import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { useCallback, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { StackedGraphApi } from "../../Api/GraphApi";
import BaseLoader from "../../BaseComponents/BaseLoader";
import { checkStatusCodeSuccess, errHandler, generateColor } from "../../Constant/common";
import { useSelectedCenter } from "../CenterContext";
import { Failed, OnHold, Selected, StaffInterviewGraph } from "../../Constant/SalesDashboard";
import { notFound } from "../../Constant";
import { endDateLabel, startDateLabel } from "../../Constant/Lead";
import { RiSearchLine } from "react-icons/ri";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const StackedBarChart = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem("sales_stacked_start_date") || "";
  });
  const [endDate, setEndDate] = useState(() => {
    return localStorage.getItem("sales_stacked_end_date") || "";
  });

  const { selectedCenterPayload } = useSelectedCenter();
  const centerId = selectedCenterPayload?.center_id;

  const handleStartDateChange = (date) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "";
    setStartDate(formattedDate);
    localStorage.setItem("sales_stacked_start_date", formattedDate);
  };

  const handleEndDateChange = (date) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : "";
    setEndDate(formattedDate);
    localStorage.setItem("sales_stacked_end_date", formattedDate);
  };

  const fetchStackedGraph = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);

    try {
      const payload = {
        center_id: centerId,
        start_date: startDate,
        end_date:
          startDate === endDate
            ? moment(endDate, "YYYY-MM-DD").add(1, "days").format("YYYY-MM-DD")
            : endDate,
      };

      const response = await StackedGraphApi(payload);

      if (checkStatusCodeSuccess(response?.statusCode)) {
        const apiData = response?.data?.response || [];

        const labels = apiData.map((item) => item.name);

        const techSet = new Set();
        apiData.forEach((item) => {
          item.interviews?.forEach((intv) => {
            techSet.add(intv.technology_name);
          });
        });
        //comment
        const technologies = Array.from(techSet);

        const datasets = technologies.map((tech, index) => ({
          label: tech,
          data: apiData.map((item) => {
            const found = item.interviews?.find((i) => i.technology_name === tech);
            return found ? found.count : 0;
          }),
          meta: apiData.map((item) => {
            const found = item.interviews?.find((i) => i.technology_name === tech);
            return {
              selected: found?.selectedCount || 0,
              onHold: found?.onHoldCount || 0,
              failed: found?.failureCount || 0,
            };
          }),
          backgroundColor: generateColor(index, technologies.length),
          stack: "stack1",
          maxBarThickness: 50,
        }));

        if (apiData && apiData.length > 0) {
          setChartData({ labels, datasets });
          setNoData(false);
        } else {
          setChartData(null);
          setNoData(true);
        }
      } else {
        setChartData(null);
        setNoData(true);
        if (
          typeof response?.message === "string" &&
          response.message.includes("center_id") &&
          response.message.includes("undefined")
        ) {
          navigate("/globalConfiguration");
        }
      }
    } catch (error) {
      setChartData(null);
      setNoData(true);
      errHandler(error);
      const errMsg = error?.response?.data?.message || error?.message || "";
      if (
        typeof errMsg === "string" &&
        errMsg.includes("center_id") &&
        errMsg.includes("undefined")
      ) {
        navigate("/globalConfiguration");
      }
    } finally {
      setLoading(false);
    }
  }, [centerId, endDate, navigate, startDate]);

  useEffect(() => {
    fetchStackedGraph();
  }, [fetchStackedGraph]);
  useEffect(() => {
    const savedStart = localStorage.getItem("sales_stacked_start_date");
    const savedEnd = localStorage.getItem("sales_stacked_end_date");

    if (!savedStart || !savedEnd) {
      const today = new Date();

      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const formatDate = (date) => moment(date).format("YYYY-MM-DD");

      const defaultStart = formatDate(monthStart);
      const defaultEnd = formatDate(monthEnd);

      setStartDate(defaultStart);
      setEndDate(defaultEnd);
      localStorage.setItem("sales_stacked_start_date", defaultStart);
      localStorage.setItem("sales_stacked_end_date", defaultEnd);
    }
  }, []);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          footer: (tooltipItems) => {
            if (!tooltipItems.length) return "";

            const item = tooltipItems[0];

            const staffIndex = item.dataIndex;
            const techIndex = item.datasetIndex;

            const meta = chartData?.datasets?.[techIndex]?.meta?.[staffIndex];

            if (!meta) return "";

            return [
              `${Selected} ${meta.selected}`,
              `${OnHold} ${meta.onHold}`,
              `${Failed} ${meta.failed}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          drawOnChartArea: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="border rounded p-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <h6 className="mb-0">{StaffInterviewGraph}</h6>

        <div className="row">
          <div className="col-lg-12 row">
            <div className="col-lg-6">
              <label className="form-label">{startDateLabel}</label>
              {/* Fixed date handling to avoid timezone offset issues – parsing YYYY-MM-DD as local date */}
              <DatePicker
                selected={startDate ? moment(startDate, "YYYY-MM-DD").toDate() : null}
                onChange={handleStartDateChange}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                maxDate={endDate ? moment(endDate, "YYYY-MM-DD").toDate() : null}
              />
            </div>
            <div className="col-lg-6">
              <label className="form-label">{endDateLabel}</label>
              {/* Fixed date handling to avoid timezone offset issues – parsing YYYY-MM-DD as local date */}
              <DatePicker
                selected={endDate ? moment(endDate, "YYYY-MM-DD").toDate() : null}
                onChange={handleEndDateChange}
                className="form-control"
                dateFormat="yyyy-MM-dd"
                minDate={startDate ? moment(startDate, "YYYY-MM-DD").toDate() : null}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 320 }}>
        {loading ? (
          <BaseLoader />
        ) : noData ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: "var(--icon-btn-bg)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RiSearchLine style={{ fontSize: 24, color: "var(--text-muted)" }} />
            </div>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.875rem" }}>
              {notFound.dataNotFound}
            </p>
          </div>
        ) : (
          chartData && <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default StackedBarChart;
